import { create } from 'zustand'

// Claves/Helpers para persistencia web segura
const LS_KEY = 'interactions_cache_v1'
const isBrowser =
    typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

function loadFromStorage(): { [postId: string]: any } {
    if (!isBrowser) return {}
    try {
        const raw = window.localStorage.getItem(LS_KEY)
        if (!raw) return {}
        const parsed = JSON.parse(raw)
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed))
            return parsed
        return {}
    } catch {
        return {}
    }
}

function saveToStorage(state: { [postId: string]: any }) {
    if (!isBrowser) return
    try {
        // Limitar tamaño básico: sólo guardar campos clave por post
        const compact: Record<string, any> = {}
        for (const [k, v] of Object.entries(state || {})) {
            const key = String(k)
            const it = v || {}
            compact[key] = {
                post_id: String(it.post_id ?? key),
                reactions: Array.isArray(it.reactions) ? it.reactions : [],
                latest_reactions: Array.isArray(it.latest_reactions)
                    ? it.latest_reactions
                    : [],
                user_reaction: Array.isArray(it.user_reaction)
                    ? it.user_reaction
                    : [],
                comment_count:
                    typeof it.comment_count === 'number' ? it.comment_count : 0,
                comment_first: it.comment_first || {},
                views: typeof it.views === 'number' ? it.views : 0,
            }
        }
        window.localStorage.setItem(LS_KEY, JSON.stringify(compact))
    } catch {}
}

interface ToggleReactionResult {
    previous: any
    current: any
}

interface InteractionState {
    interactions: { [postId: string]: any }
    _optimisticVersions: { [postId: string]: number }
    setInteractions: (newInteractions: { [postId: string]: any }) => void
    getInteraction: (postId: string) => (state: InteractionState) => any
    toggleReaction: (
        postId: string,
        reactionId: string,
        reactionsCatalog: any[],
    ) => ToggleReactionResult
    decrementOptimisticVersion: (postId: string) => void
    clearInteractions?: () => void
}

export const useInteractionsStore = create<InteractionState>((set, get) => ({
    // Hidratar desde localStorage en web para modo offline-first
    interactions: loadFromStorage(), // { [postId]: interaction }
    _optimisticVersions: {},

    setInteractions: (newInteractions) =>
        set((state) => {
            const next = { ...state.interactions, ...newInteractions }
            // Persistencia web (no afecta mobile)
            saveToStorage(next)
            return { interactions: next }
        }),

    getInteraction: (postId) => (state) => state.interactions[postId],

    toggleReaction: (
        postId: string,
        reactionId: string,
        reactionsCatalog: any[],
    ): ToggleReactionResult => {
        const state = get()
        const key = String(postId)
        const previous = state.interactions[key] || null

        // 1. Clonar o inicializar el estado actual del posteo
        let current = {
            ...(previous || {
                post_id: key,
                reactions: [],
                user_reaction: [],
                latest_reactions: [],
            }),
        }

        if (!Array.isArray(current.reactions)) {
            current.reactions = []
        }

        const prevUserReactionId =
            current.user_reaction?.[0]?.reaction_id?.toString?.() || null
        const isToggleSame =
            prevUserReactionId && prevUserReactionId === reactionId?.toString?.()

        // Encontrar la reacción dentro del catálogo autorizado
        const reactionFromCatalog = reactionsCatalog?.find(
            (r: any) =>
                (r._id || r.reaction_id)?.toString() === reactionId?.toString(),
        )

        let reactionExistsInArray = false
        let deltaTotal = 0

        // Determinar la fluctuación del total global
        if (isToggleSame) {
            deltaTotal = -1 // Remueve la reacción
        } else {
            deltaTotal = prevUserReactionId ? 0 : 1 // Si cambia un emoji por otro es 0, si es nuevo es +1
        }

        // 2. Mapear y actualizar contadores internos del array 'reactions'
        let newReactions = current.reactions.map((r: any) => {
            const rid = (r.reaction_id || r._id)?.toString()

            const isTargetReaction =
                rid === reactionId?.toString() ||
                (r.name &&
                    reactionFromCatalog?.name &&
                    r.name === reactionFromCatalog.name)

            const isPrevReaction =
                prevUserReactionId &&
                (rid === prevUserReactionId?.toString() ||
                    (r.name &&
                        current.user_reaction?.[0]?.name &&
                        r.name === current.user_reaction[0].name))

            // Si tenía otra reacción previa y la estamos removiendo
            if (isPrevReaction && !isToggleSame) {
                return {
                    ...r,
                    count: Math.max((Number(r.count) || 0) - 1, 0),
                    isSelected: false,
                }
            }

            // Si es la reacción interactuada
            if (isTargetReaction) {
                reactionExistsInArray = true
                if (isToggleSame) {
                    return {
                        ...r,
                        count: Math.max((Number(r.count) || 0) - 1, 0),
                        isSelected: false,
                    }
                } else {
                    return {
                        ...r,
                        count: (Number(r.count) || 0) + 1,
                        isSelected: true,
                    }
                }
            }

            return r
        })

        // 3. FIX CRÍTICO: Si el emoji no existía en el post, lo inyectamos al array con count: 1
        if (!isToggleSame && !reactionExistsInArray && reactionFromCatalog) {
            newReactions.push({
                ...reactionFromCatalog,
                reaction_id: reactionId,
                _id: reactionId,
                count: 1,
                isSelected: true,
            })
        }

        // 4. Construir el nuevo user_reaction
        let newUserReaction: any[] = []
        if (!isToggleSame) {
            const reactionFromStore = newReactions.find(
                (r: any) =>
                    (r.reaction_id || r._id)?.toString() === reactionId?.toString(),
            )
            newUserReaction = [
                {
                    reaction_id: reactionId,
                    _id: reactionId,
                    name:
                        reactionFromCatalog?.name ||
                        reactionFromStore?.name ||
                        'like',
                    reaction_name:
                        reactionFromCatalog?.name ||
                        reactionFromStore?.name ||
                        'like',
                    reaction_index:
                        reactionFromCatalog?.index ??
                        reactionFromCatalog?.reaction_index ??
                        reactionFromStore?.index ??
                        reactionFromStore?.reaction_index ??
                        0,
                    isSelected: true,
                },
            ]
        }

        // 5. Calcular de forma segura el nuevo gran total numérico
        const previousTotalCount =
            current.reactions_total !== undefined
                ? Number(current.reactions_total)
                : current.reactions.reduce(
                      (sum: number, r: any) => sum + (Number(r.count) || 0),
                      0,
                  )

        const newTotalCount = Math.max(0, previousTotalCount + deltaTotal)

        // 6. Mapeo de compatibilidad heredada (campo legacy liked)
        const isLikedLegacy =
            newUserReaction.length > 0 &&
            (newUserReaction[0].name === 'heart' ||
                newUserReaction[0].name === 'like')
                ? 1
                : 0

        // Ensamble de la entidad actualizada
        const updated = {
            ...current,
            post_id: key,
            reactions: newReactions,
            user_reaction: newUserReaction,
            reactions_total: newTotalCount,
            liked: isLikedLegacy,
        }

        // 7. Escritura síncrona en el estado global e incremento optimista
        set((s) => {
            const nextInteractions = { ...s.interactions, [key]: updated }
            saveToStorage(nextInteractions)
            return {
                interactions: nextInteractions,
                _optimisticVersions: {
                    ...s._optimisticVersions,
                    [key]: (s._optimisticVersions[key] || 0) + 1,
                },
            }
        })

        return { previous, current: updated }
    },

    decrementOptimisticVersion: (postId: string) => {
        set((s) => {
            const key = String(postId)
            const current = s._optimisticVersions[key] || 0
            const next = Math.max(current - 1, 0)
            const versions = { ...s._optimisticVersions }
            if (next === 0) {
                delete versions[key]
            } else {
                versions[key] = next
            }
            return { _optimisticVersions: versions }
        })
    },
    clearInteractions: () => {
        if (isBrowser) {
            window.localStorage.removeItem(LS_KEY)
        }
        set({ interactions: {}, _optimisticVersions: {} })
    },
}))
